import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import DBStatus from './DBStatus';

const { FiLogOut, FiUser, FiList, FiHome, FiUsers, FiDollarSign, FiArrowLeft, FiSettings, FiActivity, FiMenu, FiX } = FiIcons;

const Layout = () => {
  const { user, logout, originalAdmin, stopImpersonating } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
  }, [isMenuOpen]);

  // Close menu on navigation
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const NavItem = ({ to, icon: Icon, label }) => {
    const isActive = location.pathname === to;
    return (
      <button 
        type="button"
        onClick={() => navigate(to)} 
        className={`w-full flex items-center gap-4 px-6 py-3.5 rounded-2xl transition-all duration-300 group ${isActive ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-900'}`}
      >
        <div className={`p-2 rounded-xl transition-colors ${isActive ? 'bg-blue-50' : 'group-hover:bg-gray-100'}`}>
          <Icon className="text-xl" />
        </div>
        <span className="font-bold tracking-tight">{label}</span>
      </button>
    );
  };

  const Navigation = () => (
    <nav className="flex-1 space-y-1 px-2 mt-4">
      {user?.role === 'super_admin' && (
        <>
          <NavItem to="/admin" icon={FiHome} label="Dashboard" />
          <NavItem to="/admin/settings" icon={FiSettings} label="Settings" />
        </>
      )}
      {user?.role === 'master' && (
        <>
          <NavItem to="/master" icon={FiHome} label="Overview" />
          <NavItem to="/master/pilots" icon={FiUsers} label="Pilots" />
          <NavItem to="/master/activity" icon={FiActivity} label="Activity" />
          <NavItem to="/master/drops" icon={FiSettings} label="Config" />
          <NavItem to="/master/payouts" icon={FiDollarSign} label="Payouts" />
        </>
      )}
      {user?.role === 'pilot' && (
        <>
          <NavItem to="/pilot" icon={FiHome} label="Dashboard" />
          <NavItem to="/pilot/log" icon={FiList} label="Log Drop" />
        </>
      )}
    </nav>
  );

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#f5f5f7]">
      {/* Mobile Header */}
      <header className="lg:hidden flex items-center justify-between p-4 bg-white/80 backdrop-blur-md border-b sticky top-0 z-[60]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center text-white font-black text-xl">R</div>
          <span className="font-black text-gray-900">ROX Tracker</span>
        </div>
        <button 
          type="button"
          onClick={() => setIsMenuOpen(!isMenuOpen)} 
          className="p-2 bg-gray-100 rounded-xl text-gray-600 active:scale-95 transition-all relative z-[70]"
        >
          {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>
      </header>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-72 p-6 flex-col fixed h-full z-20">
        <div className="bg-white/50 rounded-[2.5rem] flex-1 p-4 flex flex-col border border-white/40 shadow-sm backdrop-blur-sm">
          <div className="px-4 py-8 flex items-center gap-4">
            <div className="w-12 h-12 bg-black rounded-2xl flex items-center justify-center text-white font-black text-2xl">R</div>
            <div>
              <h1 className="font-black text-gray-900 leading-none">ROX Tracker</h1>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">Boss Hunt OS</p>
            </div>
          </div>
          <Navigation />
          <div className="p-4 bg-white/40 rounded-3xl border border-white/50 mt-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-gray-400 border border-gray-100"><FiUser /></div>
              <p className="text-sm font-black text-gray-900 truncate">{user?.username}</p>
            </div>
            <button type="button" onClick={handleLogout} className="w-full py-3 text-xs font-black text-red-500 hover:bg-red-50 rounded-2xl transition-all">
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[55] lg:hidden"
            />
            <motion.aside 
              initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 left-0 bottom-0 w-[280px] z-[60] lg:hidden p-4"
            >
              <div className="bg-white rounded-[2rem] h-full flex flex-col border border-gray-100 shadow-2xl overflow-y-auto no-scrollbar">
                <div className="px-6 py-8 flex items-center gap-4">
                  <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center text-white font-black text-xl">R</div>
                  <span className="font-black text-gray-900 uppercase tracking-tighter">Navigation</span>
                </div>
                <Navigation />
                <div className="mt-auto p-6 bg-gray-50 rounded-b-[2rem]">
                  <button type="button" onClick={handleLogout} className="w-full py-4 text-xs font-black text-red-500 bg-red-50 rounded-2xl">
                    Logout Account
                  </button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <main className="flex-1 lg:ml-72 p-5 md:p-12 min-h-screen relative z-10">
        <div className="max-w-6xl mx-auto">
          <Outlet />
        </div>
      </main>
      <DBStatus />
    </div>
  );
};

export default Layout;